import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, AlertTriangle } from "react-feather";
import { useListStockMovementReportQuery } from "../../stockApi";
import { paths } from "../../../../routes/paths";

/*
  "Does the balance tally with the movements?" - the expanded row of Stock Balances
  (user, 2026-09-19).

  No new API: one call to the existing Stock Movement Report for this product + branch,
  from the very first movement up to today, and the sums are done here. Fetching from the
  beginning (not just the chosen period) is what makes the check honest - the opening figure
  is ADDED UP from earlier movements, not back-calculated from today's balance, so a balance
  that does not match its own history shows as a difference instead of being hidden.

  What moves stock (read from sp_app_stock_movement_report + the live data, not assumed):
    - Stock In / Stock Out / Return post immediately (POSTED / ISSUED).
    - An Adjustment moves stock only once APPROVED.
    - An LPO line NEVER moves stock, although the ledger gives it a positive SignedQty.
    - Voided documents are not fetched (includeVoided stays false).
*/

const TYPE_LABEL = {
  STOCK_IN: "Stock In",
  STOCK_OUT: "Stock Out",
  STOCK_RETURN: "Return",
  STOCK_ADJUSTMENT: "Adjustment",
};

const FETCH_SIZE = 5000;
const qty = (n) => Number(n || 0).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const day = (value) => (value ? String(value).slice(0, 10) : "");
const showDate = (iso) => (iso ? new Date(`${day(iso)}T00:00:00`).toLocaleDateString("en-GB") : "");

const movesStock = (row) =>
  row.movementType !== "LPO" && !row.isDeleted && (row.movementType !== "STOCK_ADJUSTMENT" || row.status === "APPROVED");

const drillPath = (row) => {
  switch (row.movementType) {
    case "STOCK_IN": return paths.stockInDetail(row.documentRecordGuid);
    case "STOCK_OUT": return paths.stockOutDetail(row.documentRecordGuid);
    case "STOCK_RETURN": return paths.stockReturnDetail(row.documentRecordGuid);
    case "STOCK_ADJUSTMENT": return paths.stockAdjustmentDetail(row.documentRecordGuid);
    default: return null;
  }
};

const Box = ({ label, value, sub, tone }) => (
  <div
    className="border rounded px-3 py-2 text-center"
    style={{ minWidth: 150, background: tone === "strong" ? "#f6f1f1" : "#fff", borderColor: tone === "strong" ? "#7a1f1f" : undefined }}
  >
    <div className="text-muted small">{label}</div>
    <div className="fw-bold" style={{ fontSize: 20, color: tone === "in" ? "#1e7e34" : tone === "out" ? "#b02a37" : undefined }}>{value}</div>
    {sub && <div className="text-muted" style={{ fontSize: 11 }}>{sub}</div>}
  </div>
);

const Sign = ({ children }) => <div className="fw-bold text-muted align-self-center" style={{ fontSize: 22 }}>{children}</div>;

const StockBalanceTallyPanel = ({ balance, dateFrom, dateTo }) => {
  const [showLedger, setShowLedger] = useState(true);
  const today = day(new Date().toISOString());

  const { data, isLoading, isError, error } = useListStockMovementReportQuery({
    branchId: balance.branchId,
    productCode: balance.productCode,
    dateTo: today,
    pageSize: FETCH_SIZE,
  });

  const calc = useMemo(() => {
    const all = (data?.items || []).filter((r) => r.branchId === balance.branchId);
    const counted = all.filter(movesStock).sort((a, b) => day(a.documentDate).localeCompare(day(b.documentDate)) || a.documentNumber.localeCompare(b.documentNumber));
    const pendingAdjustments = all.filter((r) => r.movementType === "STOCK_ADJUSTMENT" && !r.isDeleted && r.status !== "APPROVED");

    const before = counted.filter((r) => day(r.documentDate) < dateFrom);
    const inRange = counted.filter((r) => day(r.documentDate) >= dateFrom && day(r.documentDate) <= dateTo);
    const after = counted.filter((r) => day(r.documentDate) > dateTo);
    const sum = (rows) => rows.reduce((s, r) => s + Number(r.signedQty || 0), 0);

    const opening = sum(before);
    const totalIn = sum(inRange.filter((r) => Number(r.signedQty) > 0));
    const totalOut = -sum(inRange.filter((r) => Number(r.signedQty) < 0));
    const closing = opening + totalIn - totalOut;
    const later = sum(after);
    const calculatedNow = closing + later;
    const onHand = Number(balance.currentQty || 0);

    const byType = Object.keys(TYPE_LABEL).map((type) => {
      const rows = inRange.filter((r) => r.movementType === type);
      return {
        type,
        documents: new Set(rows.map((r) => r.documentNumber)).size,
        qtyIn: sum(rows.filter((r) => Number(r.signedQty) > 0)),
        qtyOut: -sum(rows.filter((r) => Number(r.signedQty) < 0)),
      };
    });

    let running = opening;
    const ledger = inRange.map((r) => {
      running += Number(r.signedQty || 0);
      return { ...r, running };
    });

    return {
      opening, totalIn, totalOut, closing, later, calculatedNow, onHand, byType, ledger, pendingAdjustments,
      difference: Math.round((onHand - calculatedNow) * 100) / 100,
      truncated: (data?.totalCount || 0) > all.length,
    };
  }, [data, balance, dateFrom, dateTo]);

  if (isLoading) return <div className="p-3 text-muted">Adding up the movements...</div>;
  if (isError) {
    return <div className="alert alert-warning m-2 mb-0">Could not load the movements for this product ({error?.message || "server error"}).</div>;
  }

  const tallies = calc.difference === 0;
  const unit = calc.ledger[0]?.unit || "";

  return (
    <div className="p-3" style={{ background: "#fafafa" }}>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <div className="fw-bold">
          {balance.productName} <span className="text-muted fw-normal">({balance.productCode}) - {balance.branchCode}</span>
        </div>
        <div className="text-muted small">Period {showDate(dateFrom)} to {showDate(dateTo)}{unit ? ` - quantities in ${unit}` : ""}</div>
      </div>

      {/* The whole story on one line: opening + in - out = closing. */}
      <div className="d-flex flex-wrap gap-2 align-items-stretch mb-2">
        <Box label={`Opening (before ${showDate(dateFrom)})`} value={qty(calc.opening)} sub="added up from earlier movements" />
        <Sign>+</Sign>
        <Box label="Came in" value={qty(calc.totalIn)} tone="in" />
        <Sign>-</Sign>
        <Box label="Went out" value={qty(calc.totalOut)} tone="out" />
        <Sign>=</Sign>
        <Box label={`Closing (end of ${showDate(dateTo)})`} value={qty(calc.closing)} tone="strong" />
        {calc.later !== 0 && (
          <>
            <Sign>{calc.later > 0 ? "+" : "-"}</Sign>
            <Box label="Movements after the period" value={qty(Math.abs(calc.later))} sub="up to today" />
            <Sign>=</Sign>
            <Box label="Should be on hand now" value={qty(calc.calculatedNow)} tone="strong" />
          </>
        )}
      </div>

      <div className={`alert ${tallies ? "alert-success" : "alert-danger"} d-flex align-items-center gap-2 py-2 mb-3`}>
        {tallies ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
        {tallies ? (
          <span><strong>Tallies.</strong> Stock on hand is {qty(calc.onHand)} and the movements add up to the same figure.</span>
        ) : (
          <span>
            <strong>Does not tally - difference of {qty(Math.abs(calc.difference))}.</strong> Stock on hand says {qty(calc.onHand)}, the
            movements add up to {qty(calc.calculatedNow)}. The balance is {calc.difference > 0 ? "higher" : "lower"} than its own history explains.
          </span>
        )}
      </div>

      {calc.truncated && (
        <div className="alert alert-warning py-2">This product has more than {FETCH_SIZE} movement lines - only the newest {FETCH_SIZE} were added up, so the opening figure is incomplete.</div>
      )}
      {calc.pendingAdjustments.length > 0 && (
        <div className="alert alert-info py-2">
          {calc.pendingAdjustments.length} adjustment line(s) awaiting approval ({calc.pendingAdjustments.map((r) => r.documentNumber).join(", ")}) are NOT counted - they have not moved stock yet.
        </div>
      )}

      <div className="row g-3">
        <div className="col-lg-4">
          <div className="fw-bold small mb-1">In the period, by transaction type</div>
          <table className="table table-sm table-bordered mb-0 bg-white">
            <thead>
              <tr><th>Type</th><th className="text-end">Docs</th><th className="text-end">In</th><th className="text-end">Out</th></tr>
            </thead>
            <tbody>
              {calc.byType.map((t) => (
                <tr key={t.type} className={t.documents === 0 ? "text-muted" : ""}>
                  <td>{TYPE_LABEL[t.type]}</td>
                  <td className="text-end">{t.documents}</td>
                  <td className="text-end">{t.qtyIn ? qty(t.qtyIn) : "-"}</td>
                  <td className="text-end">{t.qtyOut ? qty(t.qtyOut) : "-"}</td>
                </tr>
              ))}
              <tr className="fw-bold">
                <td>Total</td>
                <td className="text-end">{calc.byType.reduce((s, t) => s + t.documents, 0)}</td>
                <td className="text-end">{qty(calc.totalIn)}</td>
                <td className="text-end">{qty(calc.totalOut)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="col-lg-8">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div className="fw-bold small">Every movement in the period, oldest first ({calc.ledger.length})</div>
            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowLedger((v) => !v)}>{showLedger ? "Hide" : "Show"}</button>
          </div>
          {showLedger && (
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              <table className="table table-sm table-bordered mb-0 bg-white">
                <thead>
                  <tr><th>Date</th><th>Type</th><th>Document</th><th>Party / reason</th><th className="text-end">In</th><th className="text-end">Out</th><th className="text-end">Balance</th></tr>
                </thead>
                <tbody>
                  <tr className="text-muted"><td colSpan={6}>Opening</td><td className="text-end">{qty(calc.opening)}</td></tr>
                  {calc.ledger.map((r, idx) => {
                    const to = drillPath(r);
                    const signed = Number(r.signedQty || 0);
                    return (
                      // A document can carry the same product on two lines - index keeps the key unique.
                      <tr key={`${r.documentNumber}-${idx}`}>
                        <td>{showDate(r.documentDate)}</td>
                        <td>{TYPE_LABEL[r.movementType] || r.movementType}</td>
                        <td>{to ? <Link to={to} target="_blank" rel="noreferrer">{r.documentNumber}</Link> : r.documentNumber}</td>
                        <td className="text-muted">{r.supplierName || r.issuedToEmployeeName || r.issuedToCustomerName || r.customerName || r.movementReason || "-"}</td>
                        <td className="text-end" style={{ color: "#1e7e34" }}>{signed > 0 ? qty(signed) : ""}</td>
                        <td className="text-end" style={{ color: "#b02a37" }}>{signed < 0 ? qty(-signed) : ""}</td>
                        <td className="text-end fw-bold">{qty(r.running)}</td>
                      </tr>
                    );
                  })}
                  {calc.ledger.length === 0 && <tr><td colSpan={7} className="text-center text-muted">No movement in this period.</td></tr>}
                  <tr className="fw-bold"><td colSpan={6}>Closing</td><td className="text-end">{qty(calc.closing)}</td></tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockBalanceTallyPanel;
